import { Heart, Home, LogOut, Search, ShoppingBag, UserCircle } from "lucide-react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useCustomerAuthStore } from "../stores/auth-store";
import { useCartLines, useCartStore } from "../stores/cart-store";
import { useLikesStore } from "../stores/likes-store";

export function Navbar() {
  const [query, setQuery] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const lines = useCartLines();
  const cartBump = useCartStore((state) => state.bump);
  const user = useCustomerAuthStore((state) => state.user);
  const clearAuth = useCustomerAuthStore((state) => state.clear);
  const likedCount = useLikesStore((state) => Object.keys(state.liked).length);
  const navigate = useNavigate();
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const submitSearch = () => {
    const trimmed = query.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  const iconButtonClass =
    "h-11 w-11 shrink-0 rounded-md text-muted-foreground hover:text-foreground";
  const compactIconButtonClass =
    "h-11 shrink-0 rounded-md border-white/10 bg-muted/35 px-3 hover:border-accent/50 hover:bg-muted";

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!profileRef.current?.contains(event.target as Node)) setProfileOpen(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.08] bg-background/90 shadow-[0_10px_35px_rgba(0,0,0,0.22)] backdrop-blur-xl">
      <div className="container-pad grid h-[66px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 sm:h-[74px] sm:gap-6">
        <nav className="flex min-w-0 items-center gap-1.5 sm:gap-2.5">
          <Button asChild variant="ghost" size="icon" className={iconButtonClass}>
            <Link to="/" aria-label="Home">
              <Home className="h-[18px] w-[18px]" />
            </Link>
          </Button>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="hidden h-11 rounded-md px-5 text-xs uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground sm:inline-flex"
          >
            <NavLink to="/search">Browse</NavLink>
          </Button>
        </nav>
        <Link
          to="/"
          className="flex min-w-0 items-center justify-center gap-2 text-center leading-none sm:gap-3.5"
          aria-label="REVLANE home"
        >
          <img
            src="/assets/revlane-logo.png"
            alt=""
            width={682}
            height={630}
            className="h-9 w-9 shrink-0 object-contain sm:h-12 sm:w-12"
            decoding="async"
          />
          <span className="min-w-0 max-[340px]:hidden">
            <span className="block font-display text-sm font-semibold tracking-[0.16em] text-foreground sm:text-[19px] sm:tracking-[0.2em]">
              REVLANE
            </span>
            <span className="mt-1.5 hidden text-[10px] uppercase tracking-[0.24em] text-muted-foreground sm:block">
              Collector Network
            </span>
          </span>
        </Link>
        <div className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-2.5">
          <form
            className="hidden min-w-0 items-center lg:flex"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch();
            }}
          >
            <div className="relative w-[clamp(210px,18vw,280px)]">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="desktop-site-search"
                name="site-search"
                className="h-11 border-white/10 bg-muted/55 pl-10"
                placeholder="Search Skyline R34, Porsche, 1:64"
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </form>
          <nav className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-2.5">
            <div ref={profileRef} className="relative">
              <Button
                variant="ghost"
                size="icon"
                className={iconButtonClass}
                aria-label="Profile"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((current) => !current)}
              >
                <UserCircle className="h-[18px] w-[18px]" />
              </Button>
              {profileOpen ? (
                <div className="absolute right-0 mt-2 w-72 rounded-lg border border-white/[0.08] bg-card/95 p-4 text-sm shadow-[0_18px_46px_rgba(0,0,0,0.42)] backdrop-blur">
                  <div className="space-y-1 border-b border-white/[0.08] pb-3">
                    <p className="font-semibold">{user?.full_name ?? "Guest Collector"}</p>
                    <p className="truncate text-muted-foreground">{user?.email ?? "Not signed in"}</p>
                  </div>
                  <div className="flex items-center justify-between py-3 text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                      <Heart className="h-4 w-4 text-accent" />
                      Liked cars
                    </span>
                    <strong className="text-foreground">{likedCount}</strong>
                  </div>
                  {user ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-center border-white/10 bg-muted/35"
                      onClick={() => {
                        clearAuth();
                        setProfileOpen(false);
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <Button asChild variant="outline" size="sm" className={compactIconButtonClass}>
              <Link to="/cart" aria-label="Cart">
                <ShoppingBag className="h-[18px] w-[18px]" />
                <span key={cartBump} className="cart-bump min-w-3 text-center">{itemCount}</span>
              </Link>
            </Button>
          </nav>
        </div>
      </div>
      <form
        className="container-pad pb-3 pt-0.5 lg:hidden"
        onSubmit={(event) => {
          event.preventDefault();
          submitSearch();
        }}
      >
        <Input
          id="mobile-site-search"
          name="mobile-site-search"
          className="h-11 border-white/10 bg-muted/55"
          placeholder="Search diecast models"
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </form>
    </header>
  );
}
